import type { AssignmentRow } from './useAssignmentRows';
import type { ClassRef, MapClass, MatchRow } from './useCourseMatch';
import type { Attachable, Misclaim } from './useMisclaims';

/** A gradebook column, with the course title support and the teacher recognise. */
type Titled<T> = T & { course_title: string | null };
export type AttachColumn = Titled<Attachable>;
export type TitledMisclaim = Titled<Misclaim>;

/** A course some of its classes sit in, with no column for it there — the teacher has to copy it in. */
export interface MissingCourse {
  course_id: string;
  title: string | null;
  classes: ClassRef[];
}

/** What one assignment row needs, joined from the course match, the gradebook check and its findings. */
export interface RowRepair {
  /** Columns the LMS names for this assignment while another holds them — safe to take. */
  columns: TitledMisclaim[];
  /** Named for it too, but the holder is delivering: the duplicate trap, never a button. */
  trapped: TitledMisclaim[];
  /** Columns it posts to that the LMS names for someone else — the other side of `columns`. */
  holding: TitledMisclaim[];
  /** Columns in the LMS that nobody holds and this assignment should — courses it is missing. */
  attach: AttachColumn[];
  /** Courses its classes sit in that have no column for it to attach: the teacher copies it there. */
  missing: MissingCourse[];
  /** Never linked, and worth linking — the match row carries the deep link when there is one. */
  link: MatchRow | null;
  /** Classes its linked courses do not hold, where removing them is the fix. */
  unassign: number;
  /** Covered by a duplicate, per student or by linked copies serving its classes: nothing to do. */
  duplicate: boolean;
  /** The linked copies a covered duplicate relies on — where its missing scores are owed. */
  servedBy: number[];
  /** Findings only the teacher can fix in the LMS; support sends the assignment page's message. */
  teacher: string[];
}

export type RepairFilter =
  | 'needs' | 'teacher' | 'attach' | 'take_column' | 'link' | 'unassign' | 'duplicate' | 'all';

/** Finding codes with no fix on our side — the teacher changes something in the LMS. */
export const TEACHER_FIXES: Record<string, string> = {
  unpublished: 'Teacher must publish it in the LMS',
};

const hasFix = (repair: RowRepair) => [
  repair.columns.length > 0, repair.attach.length > 0, repair.link != null, repair.unassign > 0,
  repair.teacher.length > 0, repair.missing.length > 0,
].some(Boolean);

export const needsAction = (repair?: RowRepair) => repair != null && hasFix(repair);

/** Opens on the work when there is any, so the duplicates and the healthy rows are one pill away. */
export const defaultFilter = (repairs: Map<number, RowRepair> | null): RepairFilter => (
  repairs != null && Array.from(repairs.values()).some(needsAction) ? 'needs' : 'all'
);

export const matchesFilter = (filter: RepairFilter, repair?: RowRepair) => {
  switch (filter) {
    case 'needs': return needsAction(repair);
    case 'teacher': return (repair?.teacher.length ?? 0) > 0 || (repair?.missing.length ?? 0) > 0;
    case 'attach': return (repair?.attach.length ?? 0) > 0;
    case 'take_column': return (repair?.columns.length ?? 0) > 0;
    case 'link': return repair?.link != null;
    case 'unassign': return (repair?.unassign ?? 0) > 0;
    case 'duplicate': return repair?.duplicate === true;
    default: return true;
  }
};

const empty = (): RowRepair => ({
  attach: [],
  columns: [],
  duplicate: false,
  holding: [],
  link: null,
  missing: [],
  servedBy: [],
  teacher: [],
  trapped: [],
  unassign: 0,
});

/** What the LMS side of a teacher looks like, beyond the per-assignment rows. */
export interface Gradebook {
  attachable?: Attachable[];
  classes?: MapClass[];
  courseTitles?: Record<string, string | null>;
}

// AIDEV-NOTE: A class outside every one of an assignment's courses has three different fixes, and the
// count alone cannot tell them apart. Kocharian (500894): every class sat in a course on the account,
// the assignment just had no column there — unassign was the only button and the guard refused it every
// time. Jennifer Miller: the same shape, but sibling copies delivered for those classes, so unassigning
// WAS the fix. So: a column to attach wins; uncovered classes in a course become the teacher's copy;
// covered ones, and classes in no course at all, stay with the guarded unassign.
const placeCourseless = (repair: RowRepair, row: AssignmentRow, match: MatchRow, gradebook: Gradebook) => {
  const courseOf = new Map((gradebook.classes ?? []).map(room => [room.id, room.course_id]));
  const attachable = new Set(repair.attach.map(column => column.course_id));
  const covered = row.codes.includes('classes_covered_by_twins');
  const missing = new Map<string, MissingCourse>();
  let unassign = 0;

  match.courseless.forEach((room) => {
    const course = courseOf.get(room.id);

    if (course != null && attachable.has(course)) return;
    if (course == null || covered) {
      unassign += 1;
      return;
    }
    if (!missing.has(course)) {
      missing.set(course, { classes: [], course_id: course, title: gradebook.courseTitles?.[course] ?? null });
    }
    missing.get(course)?.classes.push(room);
  });

  return { missing: [...missing.values()], unassign };
};

// AIDEV-NOTE: The same precedence the assignment page uses. An unlinked assignment whose every score a
// twin already delivered is a duplicate, deep link or not: Jennifer's all-classes copy was offered "Link"
// while each section's column was correctly held by its own copy. Taking a column the LMS names for it
// wins over linking, since that is the link.
export const buildRepairs = (
  assignments: AssignmentRow[],
  matchRows: MatchRow[],
  misclaims: Misclaim[],
  gradebook: Gradebook = {},
): Map<number, RowRepair> => {
  const repairs = new Map<number, RowRepair>();
  const repairFor = (id: number) => {
    if (!repairs.has(id)) repairs.set(id, empty());

    return repairs.get(id) as RowRepair;
  };
  const matchById = new Map(matchRows.map(row => [row.assignment_id, row]));
  const titled = <T extends { course_id: string | null }>(column: T): Titled<T> => ({
    ...column,
    course_title: column.course_id == null ? null : gradebook.courseTitles?.[column.course_id] ?? null,
  });

  misclaims.map(titled).forEach((column) => {
    const owner = repairFor(column.owner.id);

    (column.displacing ? owner.trapped : owner.columns).push(column);
    column.held_by.forEach(holder => repairFor(holder.id).holding.push(column));
  });
  (gradebook.attachable ?? []).forEach(column => repairFor(column.owner.id).attach.push(titled(column)));

  assignments.forEach((row) => {
    const repair = repairFor(row.assignment_id);
    const match = matchById.get(row.assignment_id);
    const covered = row.status === 'covered_by_twin';

    const claimable = repair.columns.length > 0 || repair.attach.length > 0;

    if (match?.courses === 0 && !claimable && !covered) repair.link = match;
    if (match != null && match.courses > 0) Object.assign(repair, placeCourseless(repair, row, match, gradebook));
    repair.teacher = row.codes.filter(code => code in TEACHER_FIXES);
    repair.duplicate = covered && !needsAction(repair);
    repair.servedBy = row.served_by ?? [];
  });

  return repairs;
};
